<template>
    <div class="wrapper login-split w-100 h-100">
        <div class="login-split__panel">
        <v-card class="login-card" elevation="0" rounded="xl">
            <v-card-text class="pa-8 pa-sm-10">
                <!-- Logo -->
                <div class="text-center mb-6">
                    <router-link class="d-inline-flex align-center text-decoration-none" to="/">
                        <v-img :src="logoFull" :width="200" draggable="false"/>
                        <span class="font-weight-bold font-italic text-h4 text-primary ms-1">Go</span>
                    </router-link>
                    <p class="text-caption text-medium-emphasis font-italic mt-1 mb-0">Be in touch</p>
                </div>

                <!-- Welcome text -->
                <h2 class="text-h5 font-weight-bold text-center mb-1">Welcome back</h2>
                <p class="text-body-2 text-medium-emphasis text-center mb-6">
                    Sign in to SimpliGPS Go Portal
                </p>

                <!-- Pending approval alert -->
                <v-alert v-if="pendingMessage" type="info" variant="tonal" class="mb-4" rounded="lg" closable
                         @click:close="pendingMessage = null">
                    <div class="text-body-2 font-weight-medium">Your account is being set up</div>
                    <div class="text-body-2 mt-1">
                        We're still processing your registration. You'll receive an email when your account is ready.
                    </div>
                </v-alert>

                <!-- Login form -->
                <v-form ref="form" v-model="formData.valid" validate-on="input lazy" @submit.prevent="submit">
                    <v-text-field v-model="formData.email" :rules="[vRules.required,vRules.email]"
                                  autofocus color="primary" density="comfortable"
                                  label="Email" prepend-inner-icon="mdi-email-outline"
                                  rounded="lg" type="email" variant="outlined"/>

                    <v-text-field v-model="formData.password" :rules="[vRules.required]"
                                  :type="showPassword ? 'text' : 'password'"
                                  :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                  color="primary" density="comfortable"
                                  label="Password" prepend-inner-icon="mdi-lock-outline"
                                  rounded="lg" variant="outlined"
                                  @click:append-inner="showPassword = !showPassword"/>

                    <div class="d-flex justify-end mb-4">
                        <a class="text-caption text-primary text-decoration-none font-weight-medium cursor-pointer"
                           @click="navigateTo('forgot-password')">
                            Forgot password?
                        </a>
                    </div>

                    <v-btn :disabled="!formData.valid || processing || throttleIn > 0"
                           :loading="processing" block color="primary"
                           rounded="lg" size="large" type="submit">
                        <template v-if="throttleIn > 0">Try again in {{ throttleIn }}s</template>
                        <template v-else>Sign In</template>
                    </v-btn>
                </v-form>

                <!-- Register link -->
                <p class="text-body-2 text-center mt-6 mb-0">
                    Don't have an account?
                    <a class="text-primary text-decoration-none font-weight-bold cursor-pointer"
                       @click="navigateTo('signup-phone')">
                        Create one
                    </a>
                </p>
            </v-card-text>
        </v-card>

        <!-- Footer -->
        <div class="login-footer text-caption text-medium-emphasis">
            &copy; {{ new Date().getFullYear() }} SimpliGPS Go by NextEra IoT
        </div>
        </div>

        <!-- Decorative: the copy beside it already says what the product is. -->
        <aside class="login-split__visual" aria-hidden="true">
            <picture>
                <source :srcset="bgMobile" media="(max-width: 959px)" type="image/webp"/>
                <source :srcset="bgTablet" media="(max-width: 1279px)" type="image/webp"/>
                <source :srcset="bgDesktop" type="image/webp"/>
                <img :src="bgFallback" alt="" draggable="false"/>
            </picture>
        </aside>
    </div>
</template>

<script setup>
import router from "@/router"
import {onBeforeUnmount, reactive, ref} from 'vue'
import {send} from '@/composables/apiClient'
import {showApiError} from '@/composables/useApiError'
import logoFull from "@/assets/images/logo-full.png"
import bgDesktop from "@/assets/images/go-login-desktop.webp"
import bgTablet from "@/assets/images/go-login-tablet.webp"
import bgMobile from "@/assets/images/go-login-mobile.webp"
import bgFallback from "@/assets/images/go-login-desktop.jpg"
import {useAuthStore} from '@/stores/authStore'
import {useNavigation} from '@/composables/globals'
import {useSnackbarStore} from '@/stores/snackBarStore'

const {navigateTo} = useNavigation()
const registeredEmail = localStorage.getItem('registered_email');
if (registeredEmail) localStorage.removeItem('registered_email');
// Prefill after a password reset too (set on the Forgot-password step), so the
// user lands back on login with their email already filled in.
const resetEmail = localStorage.getItem('reset_email');
if (resetEmail) localStorage.removeItem('reset_email');

const showPassword = ref(false)
const formData = reactive({
    valid: false,
    email: registeredEmail || resetEmail || null,
    password: null,
})
const form = ref(null)
const auth = useAuthStore()
const processing = ref(false)
const pendingMessage = ref(null)
const snackbar = useSnackbarStore()
const throttleIn = ref(0)
let throttleTimer = null

const startThrottleCooldown = (seconds) => {
    const clamped = Math.max(1, Math.min(600, Math.floor(seconds || 60)))
    throttleIn.value = clamped
    if (throttleTimer) clearInterval(throttleTimer)
    throttleTimer = setInterval(() => {
        throttleIn.value--
        if (throttleIn.value <= 0) clearInterval(throttleTimer)
    }, 1000)
}

onBeforeUnmount(() => { if (throttleTimer) clearInterval(throttleTimer) })

const submit = () => {
    processing.value = true;
    send('post', 'login', formData, {
        headers: {'X-Client-Source': 'web'},
    }).then(async (response) => {
        if (response.data.success) {
            if (response.data.data.user.email_verified_at === null) {
                return navigateTo('verify-email', {user: response.data.data.user.id})
            } else if (response.data.data.force_password_change) {
                auth.login(response.data.data)
                return router.push({name: 'force-change-password'})
            } else {
                auth.login(response.data.data)
                // Pull the profile (circle_limbo / is_minor / is_circle_admin)
                // BEFORE the reload so the next render gates correctly, with no
                // flash of the full app for a minor who lands in circle limbo.
                await auth.hydrateProfile()
                // Coming from an invitation link, which is accepted on Members. A full load, like the line below.
                if (router.currentRoute.value.query.redirect === '/members') {
                    return window.location.assign(router.resolve({name: 'members'}).href)
                }
                return router.go('dashboard')
            }
        } else {
            snackbar.showSnackbar(response.data.message ?? 'Invalid Credentials', 'error')
        }
    }).catch((error) => {
        const msg = error.response?.data?.message ?? error?.data?.message
        if (error.response?.status === 403 && msg?.toLowerCase().includes('being set up')) {
            pendingMessage.value = msg
        } else {
            pendingMessage.value = null
            const info = showApiError(snackbar, error, 'Invalid Credentials')
            if (info.isThrottled) {
                startThrottleCooldown(info.retryAfterSec || 60)
            }
        }
    }).finally(() => processing.value = false)
}
</script>

<style scoped>
/*
 * Sign-in on the left, lifestyle photograph on the right (handoff v1.0).
 * Desktop  (>= 1280px): panel 40vw clamped 520-700px, photo takes the rest.
 * Tablet   (960-1279px): 50/50.
 * Mobile   (< 960px):   photo becomes a short banner above the form; it is
 *                        dropped entirely on short viewports so the button
 *                        stays above the fold.
 */
.wrapper {
    display: flex;
    background: #ffffff;
    /* The page sits in a flex layout; without this it is sized to its widest
       nowrap child and scrolls sideways. */
    min-width: 0;
    max-width: 100%;
}

.login-split__panel {
    display: flex;
    /* 552px keeps the form at its full 440px from 1380px down to the tablet
       breakpoint; below that the 50% rule takes over. */
    flex: 0 0 clamp(552px, 40vw, 700px);
    flex-direction: column;
    /* safe: when the card is taller than the panel the top stays reachable
       instead of overflowing equally in both directions. */
    justify-content: safe center;
    align-items: center;
    padding: 48px 56px;
    overflow-y: auto;
    position: relative;
}

.login-split__visual {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
}

.login-split__visual picture,
.login-split__visual img {
    display: block;
    width: 100%;
    height: 100%;
}

.login-split__visual img {
    object-fit: cover;
    /* Keep the woman, dog and SUV whole; the lake fills the left edge. */
    object-position: 72% center;
}

.login-card {
    /* Never shrink: a short window must scroll the panel, not clip the card. */
    flex: 0 0 auto;
    width: 100%;
    max-width: 440px;
    margin: auto;
    border: none;
}

.login-footer {
    flex: 0 0 auto;
    margin-top: 16px;
}

@media (max-width: 1279px) {
    .login-split__panel {
        flex: 0 0 50%;
        padding: 40px 32px;
    }

    .login-split__visual img {
        object-position: 76% center;
    }
}

@media (max-width: 959px) {
    .wrapper {
        flex-direction: column;
    }

    .login-split__panel {
        flex: 1 1 auto;
        order: 2;
        padding: 24px 16px 32px;
    }

    .login-split__visual {
        flex: 0 0 24vh;
        max-height: 240px;
        order: 1;
    }

    /* A banner crop is vertical: bias up so the subject's face stays in it. */
    .login-split__visual img {
        object-position: 76% 25%;
    }

    .login-card {
        margin: 0 auto;
    }
}

/* Short phone in landscape, or a small window: the form alone. */
@media (max-width: 959px) and (max-height: 560px) {
    .login-split__visual {
        display: none;
    }
}
</style>
